/**
 * Monta il pannello dell'assistente sopra tutta l'applicazione e tiene la
 * conversazione in corso.
 *
 * Lo stato ("c'e per questo utente?") si chiede una volta per sessione: senza
 * le variabili del fornitore, o senza il permesso, il pannello non esiste e i
 * pulsanti non compaiono. Il Live Timer resta fuori da sé, perché i suoi
 * pulsanti non passano di qui.
 *
 * La conversazione vive qui e non nel pannello: chiuderlo non la perde piu, e
 * la vista a tutta pagina (V59) continua la stessa senza tenerne una copia.
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { chiama } from '../api/client.js';
import { type Conversazione, ContestoAi, NESSUNA, type StatoAssistente } from './contesto.js';
import { PannelloAssistente } from './PannelloAssistente.js';

export function ProviderAssistente({ children }: { children: ReactNode }) {
  const [stato, setStato] = useState<StatoAssistente | null>(null);
  const [aperto, setAperto] = useState(false);
  const [conversazione, setConversazione] = useState<Conversazione>(NESSUNA);
  const [mostraLetture, setMostraLetture] = useState(false);

  const aggiornaStato = useCallback(async () => {
    try {
      setStato(await chiama<StatoAssistente>('/assistente'));
    } catch {
      // L'assistente è un di più: se lo stato non arriva, l'applicazione
      // funziona come prima e i pulsanti restano nascosti.
      setStato({ disponibile: false });
    }
  }, []);

  useEffect(() => {
    void aggiornaStato();
  }, [aggiornaStato]);

  const valore = useMemo(
    () => ({
      stato,
      apri: () => setAperto(true),
      chiudi: () => setAperto(false),
      conversazione,
      imposta: setConversazione,
      dimentica: () => setConversazione(NESSUNA),
      aggiornaStato,
      mostraLetture,
      impostaLetture: setMostraLetture,
    }),
    [stato, conversazione, aggiornaStato, mostraLetture],
  );

  return (
    <ContestoAi.Provider value={valore}>
      {children}
      {stato?.disponibile && (
        <PannelloAssistente aperto={aperto} onCambia={setAperto} domande={stato.domande} />
      )}
    </ContestoAi.Provider>
  );
}
